import { IonContent, IonPage } from '@ionic/react';
import React from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { SlotReduxCommandCreator } from 'appRedux/actions/slotCommandCreator';
import EventCard from './EventCard';
import { openChallengeExplore } from 'pages/EventsTab/Challenges/openChallengeExplore';
import ListSheetLeaderboard from '../LeaderBoardTab/ListSheetLeaderboard';
import { SnapPoint } from 'components/CasinoTabBar/views/home/views/MapStats/HotListReport/components/ListSheet';
import { CasinoService, IChallengeItem } from 'services/casino';
import { IState } from 'appRedux/createStore';
import { useHistory } from 'react-router';
import CasinoNavBar from 'components/CasinoNavBar';
import LeaderboardFeatureCard from 'pages/LeaderBoardTab/LeaderboardFeatureCard';
import FloorMapBackdrop from './FloorMapBackdrop';
import FooterLogo from 'components/FooterLogo';
import { useSortedFeaturedItems } from 'hooks/useSortedFeaturedItems';

const EventsTab: React.FC<{ isDarkMode: boolean; styles?: object }> = ({
  isDarkMode = false,
  styles,
}) => {
  const casinoSvcRef = React.useRef<CasinoService>(new CasinoService());
  const history = useHistory();
  const dispatch = useDispatch();
  const slotCommands = SlotReduxCommandCreator(dispatch);
  const casinoId = useSelector<IState>(
    (state) => state.app.persistedState.casinoSchema?.kpCasinoPk
  ) as number;
  const casinoLogoAlt = useSelector<IState>(
    (state) => state.app.persistedState.casinoSchema?.logos?.logoAlt
  ) as string | undefined;
  const isCasinoAccount = useSelector<IState>(
    (state) => state.app.persistedState.userInfo?.isCasinoAccount
  ) as boolean;
  const isSlotcheckInternal = useSelector<IState>(
    (state) => state.app.persistedState.userInfo?.groups?.includes('SlotcheckInternal')
  ) as boolean;
  const isPrivilegedAccount = isCasinoAccount || isSlotcheckInternal;
  const [featuredItems, setFeaturedItems] = React.useState<IChallengeItem[]>([]);
  const [isFeaturedLoading, setIsFeaturedLoading] = React.useState<boolean>(false);
  const [activeIndex, setActiveIndex] = React.useState(0);
  const carouselRef = React.useRef<HTMLDivElement>(null);

  const sortedFeaturedItems = useSortedFeaturedItems(featuredItems);

  React.useEffect(() => {
    if (!casinoId) {
      setFeaturedItems([]);
      setIsFeaturedLoading(false);
      return;
    }
    setIsFeaturedLoading(true);
    const sub = casinoSvcRef.current.GetChallenges(casinoId, isPrivilegedAccount).subscribe({
      next: (resp) => {
        setFeaturedItems(Array.isArray(resp?.featured) ? resp.featured : []);
        setIsFeaturedLoading(false);
      },
      error: () => {
        setFeaturedItems([]);
        setIsFeaturedLoading(false);
      },
    });

    return () => sub.unsubscribe();
  }, [casinoId, isPrivilegedAccount]);

  const handleCarouselScroll = () => {
    const el = carouselRef.current;
    if (!el) return;
    setActiveIndex(Math.round(el.scrollLeft / el.clientWidth));
  };

  return (
    <IonPage className="events-tab">
      <IonContent className="events-tab__content" fullscreen>
        <CasinoNavBar styles={styles} isDarkMode={isDarkMode} />

        <FloorMapBackdrop />

        <ListSheetLeaderboard initialSnap={SnapPoint.Full}>
          {!isFeaturedLoading && sortedFeaturedItems.length > 0 && (
            <div className="events-hero__tabs">
              <span className="events-hero__tab events-hero__tab--active">Feature</span>
              <div className="event-underline" />
            </div>
          )}

          <div className="events-section events-section--banner">
            {isFeaturedLoading ? (
              <div className="leaderboard-mapstats__loader" role="status" aria-live="polite">
                <span className="leaderboard-table__spinner" aria-hidden="true" />
                <span>Loading challenges...</span>
              </div>
            ) : sortedFeaturedItems.length > 0 ? (
              <div className="featured-carousel-wrapper">
                <div
                  className="featured-carousel"
                  ref={carouselRef}
                  onScroll={handleCarouselScroll}
                >
                  {sortedFeaturedItems.map((item) => (
                    <div key={item.uuid || item._id} className="featured-carousel__slide">
                      <EventCard
                        title={<>{item.name}</>}
                        subtitle={item.description}
                        descriptions={item.descriptions}
                        justJoined={item.joined}
                        ctaLabel="Learn More"
                        onCtaClick={() => {
                          const challengeId = String(item?.uuid || item?._id || '');
                          if (challengeId) history.push(`/tabs/join-challenge/${challengeId}`);
                        }}
                        onExploreClick={() => openChallengeExplore(slotCommands, history, item, null, casinoId)}
                        casinoLogoSrc={casinoLogoAlt}
                        variant="feature"
                        styleData={item.challenge?.styleData}
                        participantStatus={item.participantStatus}
                        startDate={item.startDate}
                        endDate={item.endDate}
                      />
                    </div>
                  ))}
                </div>
                {sortedFeaturedItems.length > 1 && (
                  <div className="featured-carousel__dots">
                    {sortedFeaturedItems.map((_, i) => (
                      <span
                        key={i}
                        className={`featured-carousel__dot${i === activeIndex ? ' featured-carousel__dot--active' : ''}`}
                      />
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <div
                className="events-section__default-leaderboard"
                onClick={() => history.push('/tabs/leaderboard', { showBackArrow: true })}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    history.push('/tabs/leaderboard', { showBackArrow: true });
                  }
                }}
              >
                <LeaderboardFeatureCard showExploreButton={false} />
              </div>
            )}
          </div>

          {!isFeaturedLoading && sortedFeaturedItems.length > 0 && (
            <div className="events-hero__tabs">
              <span className="events-hero__tab events-hero__tab--active">Events</span>
              <div className="event-underline" />
            </div>
          )}

          {!isFeaturedLoading && (
            <div className="events-section events-section--list">
              {/* <div className="events-section__header">Events</div>
              <div className="event-underline" /> */}

              <EventCard
                title={
                  <>
                    PLAYER
                    <br />
                    LEADERBOARDS
                  </>
                }
                variant="list"
                backslash={false}
                className="event-card--backslash-yellow event-card--events-leaderboard"
                onCardClick={() => history.push('/tabs/leaderboard', { showBackArrow: true })}
              />

              <EventCard
                title={
                  <>
                    SLOT
                    <br />
                    CHALLENGES
                  </>
                }
                variant="list"
                backslash={false}
                className="event-card--backslash-yellow event-card--events-slot"
                onCardClick={() => history.push('/tabs/challenges', { showBackArrow: true })}
              />
            </div>
          )}
        </ListSheetLeaderboard>

        <FooterLogo />
      </IonContent>
    </IonPage>
  );
};

export default EventsTab;
